import { FC, useState } from "react";
import Song from "../models/song";
import { Box, Button, Card, IconButton, Link, Stack, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import posthog from "posthog-js";

type Props = {
	song?: Song;
	variant?: 'elevation' | 'outlined' | undefined
	maxWidth?: number | string;
	includeIMDB?: boolean;
}

const AttributionLanguage: FC<Props> = ({ song, variant, maxWidth, includeIMDB }) => {
	const theme = useTheme();

	const [copied, setCopied] = useState(false);

	const copyAttributionToClipboard = () => {
		const attribution = `Title: "${song?.title || '[SONG TITLE(S)]'}"\nArtist: ${song?.artists && song.artists.length ? song.artists[0].name : 'Tim Kulig'} (timkulig.com)\nLicensed under Creative Commons By Attribution 4.0\nhttp://creativecommons.org/licenses/by/4.0/\nIMDB: https://www.imdb.com/name/nm0997280/?ref_=fn_al_nm_1`;

		if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
			navigator.clipboard
				.writeText(attribution)
				.then(() => {
					setCopied(true);

					posthog.capture('song_attribution_copied', {
						songId: song?.id || '[unknown]',
						songTitle: song?.title || '[unknown]',
						songAlbum: song?.album?.name || '',
						origin: 'song_dialog'
					});

					// Reset the copied state after 3 seconds
					setTimeout(() => { setCopied(false); }, 3000);
				})
				.catch(err => {
					fallbackCopyTextToClipboard(attribution);
				});
		} else {
			fallbackCopyTextToClipboard(attribution);
		}
	};

	const fallbackCopyTextToClipboard = (text: string) => {
		const textArea = document.createElement('textarea');

		// Style the textarea so it doesn't appear in the UI
		textArea.style.position = 'fixed'; // Avoid scrolling to the bottom of the page
		textArea.style.left = '-9999px';

		document.body.appendChild(textArea);
		textArea.contentEditable = 'true';
		textArea.value = text;

		const range = document.createRange();
		range.selectNodeContents(textArea);

		const selection = window.getSelection();

		if (selection) {
			selection.removeAllRanges();
			selection.addRange(range);
		}
		
		textArea.setSelectionRange(0, text.length);

		try {
			const successful = document.execCommand('copy');
			if (successful) {
				console.log('Fallback: Copying text command was successful');
			} else {
				console.error('Fallback: Copying text command failed');
			}
		} catch (err) {
			console.error('Fallback: Unable to copy', err);
		}

		document.body.removeChild(textArea);

		// Update the state to reflect the copied status
		setCopied(true);
		setTimeout(() => setCopied(false), 3000);
	};

	return (
		<Card variant={variant ? variant : 'outlined'} sx={{ p: 2, maxWidth: maxWidth ? maxWidth : 'unset' }}>
			<Stack direction="row" flexWrap="wrap" gap={2}>
				<Box flex="1 1 auto" position="relative">
					<Typography variant="caption">
						<Typography variant="caption" color="GrayText">Title:</Typography> "{song?.title || 'SONG TITLE(S) HERE'}"<br />
						<Typography variant="caption" color="GrayText">Artist:</Typography> {song?.artists && song.artists.length ? song.artists[0].name : 'Tim Kulig'} (<Link href="https://timkulig.com/" target="_blank">timkulig.com</Link>)<br />
						Licensed under Creative Commons By Attribution 4.0<br />
						<Link href="http://creativecommons.org/licenses/by/4.0/" target="_blank" sx={{ overflowWrap: 'anywhere' }}>http://creativecommons.org/licenses/by/4.0/</Link><br />
						{includeIMDB && (
							<>
								<Typography variant="caption" color="GrayText">IMDB:</Typography> <Link href="https://www.imdb.com/name/nm0997280/?ref_=fn_al_nm_1" target="_blank">https://www.imdb.com/name/nm0997280/?ref_=fn_al_nm_1</Link>
							</>
						)}
					</Typography>
					<Box
						sx={{
							position: 'absolute',
							top: -16,
							right: -16,
							display: { xs: 'block', sm: 'block', md: 'none', lg: 'none', xl: 'none' }
						}}
					>
						<IconButton
							size="small"
							sx={{
								width: 48,
								height: 48
							}}
							onClick={copyAttributionToClipboard}
						>
							<FontAwesomeIcon icon={{ prefix: 'fas', iconName: copied ? 'thumbs-up' : 'copy' }} style={{ color: theme.palette.primary.main }} />
						</IconButton>
					</Box>
				</Box>
				<Box flex="0 0 auto" sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block', xl: 'block' } }}>
					<Button
						variant="outlined"
						size="small"
						startIcon={<FontAwesomeIcon icon={{ prefix: 'fas', iconName: copied ? 'thumbs-up' : 'copy' }} />}
						onClick={copyAttributionToClipboard}
						sx={{ '&::after': { backgroundColor: theme.palette.background.default } }}
					>
						<Typography variant="button">{copied ? 'Copied!' : 'Copy'}</Typography>
					</Button>
				</Box>
			</Stack>
		</Card>
	);
};

export default AttributionLanguage;
